import React, { useState, useEffect } from 'react';
import { Box, TextField, IconButton, Dialog, DialogActions, Avatar, DialogContent, DialogContentText, Button, Drawer, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Reply from '@mui/icons-material/Send';
import SelfImprovementRounded from '@mui/icons-material/SelfImprovementRounded';
import MeditationSettings from './MeditationSettings';
import MeditationLimitNotice from '../ads/MeditationLimitNotice'
import { v4 as uuidv4 } from 'uuid';
import useDeviceInfo from '../../utils/useDeviceInfo';
import { useSocket } from '../../contexts/SocketContext'; // Utilisation de SocketContext
import { useTranslation } from 'react-i18next'; // Importer le hook i18n

const MessageInput = ({ roomCode, messages, currentUser, inviteMessageRef }) => {
  const [message, setMessage] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentSettings, setCurrentSettings] = useState(null);
  const [meditationState, setMeditationState] = useState('completed'); // Default to 'completed'
  const [existingInvite, setExistingInvite] = useState(null);
  const { paddingTop, paddingBottom } = useDeviceInfo();
  const navigate = useNavigate();
  const socket = useSocket(); // Récupération de socket depuis le contexte
  const { t } = useTranslation(); // Hook pour les traductions
  const [isFreemiumDrawerOpen, setIsFreemiumDrawerOpen] = useState(false);

  useEffect(() => {
    if (socket) {
      // Écouter les messages reçus
      socket.on('receiveMessage', (msg) => {
        let parsedMessage;

        try {
          parsedMessage = JSON.parse(msg.message);
        } catch (error) {
          parsedMessage = { text: msg.message };  // Si non JSON, considérer comme texte simple
        }

        if (parsedMessage.isInvite) {
          setExistingInvite({ ...msg, ...parsedMessage }); // Stocker l'invitation si c'est un message d'invitation
        }
      });
    }

    return () => {
      if (socket) {
        socket.off('updateMeditationState');
        socket.off('receiveMessage');
      }
    };
  }, [socket, roomCode]);

const handleMeditateClick = async () => {
  if (meditationState === 'pending' || meditationState === 'progress') {
    setShowInviteModal(true);
    return;
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/check-credits`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    const data = await response.json();

    const hasUnlimited = data.subscriptionStatus === 'active';
    const hasCredits = data.monthlyMeditationCredit > 0;

    if (hasUnlimited || hasCredits) {
      setIsDrawerOpen(true); // ouvrir les réglages classiques
    } else {
      setIsFreemiumDrawerOpen(true); // ouvrir le drawer freemium
    }
  } catch (error) {
    console.error('Erreur lors de la vérification des crédits :', error);
  }
};


  const handleModifyInvite = () => {
    if (existingInvite) {
      setCurrentSettings({
        meditationType: existingInvite.meditationType,
        soundBackground: existingInvite.soundBackground,
        duration: existingInvite.duration,
      });
    }
    setIsDrawerOpen(true);
    setShowInviteModal(false);
  };

  const handleSubmitSettings = (settings) => {
    if (settings.meditationType && settings.soundBackground !== undefined && settings.duration) {
      if (socket) {
        const inviteId = uuidv4();  // Générer un ID unique pour l'invitation
        socket.emit('startMeditation', {
          room: roomCode,
          meditationType: settings.meditationType,
          soundBackground: settings.soundBackground,
          duration: settings.duration,
          inviteId,  // Envoyer inviteId avec l'événement
        });
      }
    }
    setIsDrawerOpen(false);
  };

  const sendMessage = () => {
    if (message && socket) {
      socket.emit('sendMessage', { room: roomCode, message, sender: currentUser.userId });
      setMessage('');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box sx={{ position:'fixed', bottom:'-1px', width: '100%', backgroundColor:'white', pb: paddingBottom + 2, pt:1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mx:2, backgroundColor: '#eee', borderRadius: '20px', padding: '4px', pl: '10px'}}>
        <TextField
          variant="outlined"
          placeholder={t('message_placeholder')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
              backgroundColor: '#eee',
              '& fieldset': {
                border: 'none',
              },
              '&:hover fieldset': {
                border: 'none',
              },
              '&.Mui-focused fieldset': {
                border: 'none',
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '10px 12px',
            },
          }}
        />
        <IconButton
          onClick={sendMessage}
          disabled={!message}
          sx={{
            marginLeft: '8px',
            color: 'black',
            backgroundColor: '#EEEEEE',
            '&:hover': {
              backgroundColor: '#DDDDDD',
            },
          }}
        >
          <Reply sx={{ fontSize: '20px' }} />
        </IconButton>
        <IconButton
          onClick={handleMeditateClick}
          sx={{
            marginLeft: '4px',
            color: 'white',
            backgroundColor: '#000',
            '&:hover': {
              backgroundColor: '#333',
            },
          }}
        >
          <SelfImprovementRounded />
        </IconButton>
      </Box>

      <Dialog
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      >
        <DialogContent sx={{ px:3, pt:3, pb:0}}>
          <DialogContentText fontWeight='bold' color='black'>
            {meditationState === 'pending' && t('pending_invitation')}
            {meditationState === 'progress' && t('in_progress')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb:3, px:3}}>
          {meditationState === 'pending' && (
            <Button onClick={handleModifyInvite} variant="contained" color="primary">
              {t('modify')}
            </Button>
          )}
          {meditationState === 'progress' && (
            <>
              <Button 
                onClick={() => navigate(`/room/${roomCode}/live`)}
                variant="contained" 
                color="primary"
              >
                {t('join')}
              </Button>
              <Button 
                onClick={() => {
                  if (socket) {
                    socket.emit('endMeditation', { room: roomCode });
                    setMeditationState('completed');
                    setShowInviteModal(false);
                  }
                }} 
                color="error"
                sx={{ ml: 2 }}
              >
                {t('reset')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="bottom"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      >
        <MeditationSettings 
          onSubmit={handleSubmitSettings} 
          initialSettings={currentSettings}
          roomCode={roomCode}
        />
      </Drawer>

    <Drawer
      anchor="bottom"
      open={isFreemiumDrawerOpen}
      onClose={() => setIsFreemiumDrawerOpen(false)}
    >
      <MeditationLimitNotice onClose={() => setIsFreemiumDrawerOpen(false)} />
    </Drawer>

    </Box>
  );
};

export default MessageInput;
